import React, { useCallback, useRef } from 'react';
import type { SearchResult } from '@amzn/das-finsuite-shared-ui';
import { CreateOrUpdateApplicationFavoritesMutation } from 'src/API';
import { fetchGraphQLRequest } from 'src/api/graphql-api';
import { appSyncOperationName } from 'src/constants/AppConstants';
import * as mutations from 'src/graphql/mutations';
import { logger } from 'src/logger';
import { useAppContext } from 'src/components/context/AppContextProvider';
import { useAuth } from 'src/components/context/AuthContextProvider';
import { UserActionsEventTypes } from 'src/components/das-finsuite/das-finsuite-constants';

export interface UseFavoriteToggleReturn {
  toggleFavorite: (e: React.MouseEvent, result: SearchResult) => void;
}

export const useFavoriteToggle = (): UseFavoriteToggleReturn => {
  const { Alias: userAlias } = useAuth();
  const { favoriteApplications, setFavoriteApplications } = useAppContext();
  const pendingSaves = useRef(new Set<string>());
  const favoritesRef = useRef(favoriteApplications);
  favoritesRef.current = favoriteApplications;

  const toggleFavorite = useCallback(
    async (e: React.MouseEvent, result: SearchResult) => {
      e.stopPropagation();
      if (pendingSaves.current.has(result.id)) return;
      pendingSaves.current.add(result.id);

      const isCurrentlyFavorite = favoriteApplications.includes(result.id);
      const updatedFavorites = isCurrentlyFavorite ? favoriteApplications.filter((id) => id !== result.id) : [...favoriteApplications, result.id];

      logger.info(`DaS FinSuite -> User Action: ${isCurrentlyFavorite ? 'Unfavorite' : 'Favorite'} App from Search`, {
        event_type: isCurrentlyFavorite ? UserActionsEventTypes.TOP_NAV_UNFAVORITE_APP : UserActionsEventTypes.TOP_NAV_FAVORITE_APP,
        value: result.label
      });
      setFavoriteApplications(updatedFavorites);

      try {
        await fetchGraphQLRequest<CreateOrUpdateApplicationFavoritesMutation>({
          query: mutations.createOrUpdateApplicationFavorites,
          variables: { input: { userAlias, applicationId: updatedFavorites } },
          requestType: appSyncOperationName.MUTATION,
          requestName: 'createOrUpdateApplicationFavorites'
        });
      } catch (error: unknown) {
        logger.error('Error updating favorites from search', error as Error);
        const current = favoritesRef.current;
        const rolledBack = isCurrentlyFavorite ? [...current, result.id] : current.filter((id) => id !== result.id);
        setFavoriteApplications(rolledBack);
      } finally {
        pendingSaves.current.delete(result.id);
      }
    },
    [favoriteApplications, setFavoriteApplications, userAlias]
  );

  return { toggleFavorite };
};
